import { AnimatePresence, m, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

type CharacterBackgroundProps = {
  url?: string;
  colorSeed?: string;
  scrimOpacity?: number;
  blurPx?: number;
  parallax?: boolean;
  className?: string;
  testId?: string;
};

const hueFromSeed = (seed: string): number => (seed.charCodeAt(0) * 17) % 360;

const buildGradient = (hue: number): string =>
  `radial-gradient(circle at 30% 20%, hsl(${hue} 70% 78%) 0%, transparent 60%), radial-gradient(circle at 80% 90%, hsl(${(hue + 60) % 360} 70% 70%) 0%, transparent 60%), hsl(var(--brand-bg))`;

export const CharacterBackground = ({
  url,
  colorSeed,
  scrimOpacity = 0.55,
  blurPx = 32,
  parallax = false,
  className,
  testId,
}: CharacterBackgroundProps) => {
  const prefersReduced = useReducedMotion();
  const parallaxEnabled = parallax && !prefersReduced;
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollY } = useScroll({
    container: parallaxEnabled ? containerRef : undefined,
  });
  const parallaxY = useTransform(scrollY, [0, 600], [0, 60]);
  const hue = colorSeed ? hueFromSeed(colorSeed) : 220;
  const gradient = buildGradient(hue);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      data-testid={testId}
      className={`pointer-events-none absolute inset-0 z-0 overflow-hidden ${className ?? ""}`}
    >
      <div
        className="absolute inset-0 h-full w-full"
        style={{
          background: gradient,
          filter: `blur(${blurPx / 2}px)`,
          transform: "scale(1.1)",
        }}
      />
      <AnimatePresence initial={false}>
        {url && (
          <m.img
            key={url}
            src={url}
            alt=""
            loading="eager"
            className="absolute inset-0 h-full w-full object-cover"
            style={{
              filter: `blur(${blurPx}px) saturate(1.1)`,
              transform: "scale(1.18)",
              y: parallaxEnabled ? parallaxY : 0,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: prefersReduced ? 0 : 0.32 }}
          />
        )}
      </AnimatePresence>
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(180deg, hsl(var(--brand-bg) / ${Math.min(1, scrimOpacity + 0.25)}) 0%, hsl(var(--brand-bg) / ${Math.max(0, scrimOpacity - 0.15)}) 35%, hsl(var(--brand-bg) / ${scrimOpacity}) 60%, hsl(var(--brand-bg) / ${Math.min(1, scrimOpacity + 0.3)}) 100%)`,
        }}
      />
    </div>
  );
};

export default CharacterBackground;
